import {
  SanityHeader,
  SanityHomePageConnection,
  SanityLink,
  SanityMainNavigationNode,
  SanitySearchResultsPage,
  SanitySecondaryNavigationNode,
} from '../../types/generated/graphql-types'
import { MainNavigationNode, SecondaryNavigationNode } from '../components/Header'
import { HeaderProps } from '../components/Header/Header'
import { SitePickerProps, TopNavBarProps } from '../components/Header/TopNavBar'
import { PageContextValues } from '../context/PageContext'
import { notEmpty } from '../utilities/filters'
import { getCurrentPageLanguageVersions } from '../utilities/localization'
import { GROUP_SITE_URL, IS_EQT_MARKET, MARKET_SITE_LINKS } from '../utilities/site'
import { mapSanityLinkToInternalExternalLink } from './LinkMapper'

export interface MapSanityHeaderToHeaderPropsArgs {
  currentPageUrl: string
  header?: SanityHeader
  homePageLanguageVersions?: SanityHomePageConnection
  homePageUrl: string
  pageContext: PageContextValues
  searchResultsPage?: SanitySearchResultsPage
}

const sanityMainNavigationNodeToMainNavigationNode = (
  node: SanityMainNavigationNode
): MainNavigationNode => ({
  link: mapSanityLinkToInternalExternalLink(node.link as SanityLink),
  renderSubitemsInline: node.showSubitemsInline ?? undefined,
  subitems:
    node.subitems?.filter(notEmpty)?.map(sanityMainNavigationNodeToMainNavigationNode) ??
    [],
})

const sanitySecondaryNavigationNodeToSecondaryNavigationNode = (
  node: SanitySecondaryNavigationNode
): SecondaryNavigationNode => ({
  link: mapSanityLinkToInternalExternalLink(node.link as SanityLink),
})

const getSitePickerProps = (header: SanityHeader | undefined): SitePickerProps => {
  const activeItem = MARKET_SITE_LINKS.find(
    link => link.url === process.env.GATSBY_BASE_URL
  )

  return {
    activeItemId: activeItem?.id,
    buttonLabel: header?.gotoGroupSiteButtonLabel ?? undefined,
    buttonUrl: IS_EQT_MARKET ? GROUP_SITE_URL : undefined,
    dropdownItems: MARKET_SITE_LINKS,
    dropdownLabel: header?.sitePickerLabel ?? undefined,
    dropdownLabelMobile: header?.sitePickerLabelMobile ?? undefined,
    dropdownPlaceholder: header?.sitePickerPlaceholder ?? activeItem?.label,
  }
}

export const mapSanityHeaderToHeaderProps = ({
  currentPageUrl,
  header,
  homePageLanguageVersions,
  homePageUrl,
  pageContext,
  searchResultsPage,
}: MapSanityHeaderToHeaderPropsArgs): HeaderProps => {
  const languageVersions = getCurrentPageLanguageVersions({
    homePageLanguageVersions:
      homePageLanguageVersions?.nodes?.filter(notEmpty).map(languageVersion => ({
        language: languageVersion.language ?? 'en',
        path: languageVersion.path ?? '',
      })) ?? [],
    languageVersions:
      pageContext.languageVersions?.filter(notEmpty).map(languageVersion => ({
        language: languageVersion.language ?? 'en',
        path: languageVersion.path ?? '',
      })) ?? [],
  })

  const topNavBarProps: TopNavBarProps | undefined =
    !!languageVersions.length || !!MARKET_SITE_LINKS.length
      ? {
          languageSelectProps: {
            languageVersions,
          },
          sitePickerProps: getSitePickerProps(header),
        }
      : undefined

  return {
    currentPageUrl,
    homePageUrl,
    mainNavigationNodes:
      header?.mainNavigationNodes
        ?.filter(notEmpty)
        ?.filter(node => !!node.link)
        ?.map(sanityMainNavigationNodeToMainNavigationNode) ?? [],
    pillLink1: header?.pillLink1
      ? mapSanityLinkToInternalExternalLink(header.pillLink1)
      : undefined,
    pillLink2: header?.pillLink2
      ? mapSanityLinkToInternalExternalLink(header.pillLink2)
      : undefined,
    searchResultsPageUrl: searchResultsPage?.path ?? undefined,
    secondaryNavigationNodes:
      header?.secondaryNavigationNodes
        ?.filter(notEmpty)
        ?.filter(node => !!node.link)
        ?.map(sanitySecondaryNavigationNodeToSecondaryNavigationNode) ?? [],
    topNavBarProps,
  }
}
